import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Box, Button, Theme, useMediaQuery } from "@mui/material";
import { useAccountsId } from "@rentablo/api/apiv1";
import { useUpdateAccount } from "@api/v1/hooks/useAccounts";
import { getUpdateBankConnectionUrl, useMandatorVisibility } from "@helpers";
import { DeleteAccountDialog } from "@features/accounts/edit/components/DeleteAccountDialog";
import { ActionIconButton, ResponsiveDropdown } from "@components";
import UpdateIcon from "@icons/myicons/refresh.svg?react";
import AddIcon from "@icons/myicons/plus.svg?react";
import DotsIcon from "@icons/myicons/dots.svg?react";
import { useDepotMenuItems } from "./useDepotMenuItems";

export const DepotPageActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { depotId } = useParams<"depotId">();
  const parsedDepotId = Number.parseInt(depotId!, 10);
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const { data: account } = useAccountsId({ id: parsedDepotId });
  const { mutateAsync: updateMutateAsync } = useUpdateAccount();

  const showUpdateAsPrimaryAction = useMandatorVisibility({
    showFor: ["kpmg-germany", "rentablo", "depotcheck_ai"],
  });

  const [deleteModalShow, setDeleteModalShow] = useState(false);

  const handleHideAccount = useCallback(async () => {
    if (!account) return;
    await updateMutateAsync({
      id: parsedDepotId,
      isHidden: !account.isHidden,
      type: account.type!,
      name: account.name,
    });
    navigate("/depots-accounts");
  }, [account, parsedDepotId, updateMutateAsync, navigate]);

  const handleOpenDeleteModal = useCallback(() => setDeleteModalShow(true), []);

  const menuItems = useDepotMenuItems({
    account,
    handleHideAccount,
    handleOpenDeleteModal,
  });

  const updateConnectionUrl = getUpdateBankConnectionUrl(account);

  const handlePrimaryAction = () => {
    if (!account?.id) return;
    if (showUpdateAsPrimaryAction) {
      navigate(
        updateConnectionUrl ?? `/new-transaction-wizard?depotId=${account.id}`
      );
    } else {
      navigate(`/depots/${account.id}/file-drop`);
    }
  };

  const primaryLabel = showUpdateAsPrimaryAction
    ? t(
        updateConnectionUrl
          ? "depotPage.headerMenu.update"
          : "depotPage.headerMenu.addButton"
      )
    : t("depotPage.headerMenu.upload");

  const primaryIcon = showUpdateAsPrimaryAction ? (
    updateConnectionUrl ? (
      <UpdateIcon height="22px" width="22px" />
    ) : (
      <AddIcon width="18px" height="12px" />
    )
  ) : undefined;

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
        {!isSm && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={primaryIcon}
            onClick={handlePrimaryAction}
          >
            {primaryLabel}
          </Button>
        )}
        <ResponsiveDropdown
          id="depot-actions-menu"
          mobileTitle={account?.name}
          items={menuItems}
          anchor={
            <ActionIconButton
              data-testid="open-menu-button"
              color="tertiary"
              size="small"
            >
              <DotsIcon />
            </ActionIconButton>
          }
        />
      </Box>
      <DeleteAccountDialog
        isOpen={deleteModalShow}
        onClose={() => setDeleteModalShow(false)}
        accountId={parsedDepotId}
        type="depot"
      />
    </>
  );
};
